// TrendlineControls Component
// Handles user interaction for selecting sensors and metrics to add trendlines to the chart
// Provides dropdowns for base value (sensor) and stats (metric), and an 'Add Trendline' button
import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface TrendlineControlsProps {
  chartType: 'temperature' | 'power' | 'delta'; // Type of chart this control is for
  selectedSensor: string; // Currently selected sensor
  setSelectedSensor: (sensor: string) => void; // Function to update selected sensor
  selectedMetric: string; // Currently selected metric
  setSelectedMetric: (metric: string) => void; // Function to update selected metric
  availableSensors: string[]; // List of available sensors
  availableMetrics: string[]; // List of available metrics for the selected sensor
  onAddTrendline: () => void; // Function to add a new trendline
  trendlines: Array<{
    sensor: string;
    metric: string;
    data: any;
    chartType: 'temperature' | 'power' | 'delta';
  }>;
  onRemoveTrendline: (index: number) => void; // Function to remove a trendline
}

export function TrendlineControls({
  chartType,
  selectedSensor,
  setSelectedSensor,
  selectedMetric,
  setSelectedMetric,
  availableSensors,
  availableMetrics,
  onAddTrendline,
  trendlines,
  onRemoveTrendline
}: TrendlineControlsProps) {
  // Filter trendlines for this specific chart type
  const relevantTrendlines = trendlines.filter(t => t.chartType === chartType);

  return (
    <div className="flex items-center gap-2">
      {/* Dropdown for selecting the base value (sensor) */}
      <Select
        value={selectedSensor}
        onValueChange={setSelectedSensor}
      >
        <SelectTrigger className="w-[200px]">
          <SelectValue placeholder="Select sensor" />
        </SelectTrigger>
        <SelectContent>
          {availableSensors.map((sensor) => (
            <SelectItem key={sensor} value={sensor}>
              {sensor}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Dropdown for selecting the stat (metric) */}
      {selectedSensor && (
        <Select value={selectedMetric} onValueChange={setSelectedMetric}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Select stat" />
          </SelectTrigger>
          <SelectContent>
            {availableMetrics.map((metric) => (
              <SelectItem key={metric} value={metric}>
                {metric}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Button to add a new trendline */}
      <Button
        onClick={onAddTrendline}
        disabled={!selectedSensor || !selectedMetric}
        className="min-w-[120px]"
        variant="thalo"
      >
        Add Trendline
      </Button>

      {/* Display active trendlines for this chart type */}
      {relevantTrendlines.length > 0 && (
        <div className="flex flex-col gap-2 mt-2">
          <h3 className="text-sm font-medium">Active Trendlines:</h3>
          {relevantTrendlines.map((trendline, index) => (
            <div key={`${trendline.sensor}_${trendline.metric}`} className="flex items-center gap-2">
              <span className="text-sm">
                {trendline.sensor} {trendline.metric} ({trendline.data.rate_of_change.toFixed(1)}%/{trendline.data.denominator})
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onRemoveTrendline(index)}
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
} 